// Der manuelle Umschalter. Drei Zustaende, nicht zwei:
//
//   'system'  — kein data-theme am <html>. prefers-color-scheme entscheidet, wie es soll.
//   'hell'    — data-theme="light". Schlaegt ein dunkel gestelltes Betriebssystem.
//   'dunkel'  — data-theme="dark".  Schlaegt ein hell gestelltes Betriebssystem.
//
// Ein zweiwertiger Schalter kann "gib die Entscheidung an mein System zurueck" nicht ausdruecken,
// und genau das braucht jemand, der abends automatisch auf dunkel wechselt.
//
// Jeder Zugriff auf localStorage steckt in try/catch. In einem privaten Fenster und bei
// blockierten Website-Daten wirft schon das Lesen eine SecurityError-Ausnahme; eine Anwendung, in
// der Menschen ihr Geld sehen, darf daran nicht scheitern. Der Preis ist, dass die Wahl dann nur
// fuer diese Sitzung gilt — das ist die richtige Reihenfolge der Uebel.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

export type Farbschema = 'system' | 'hell' | 'dunkel'

export const FARBSCHEMA_SCHLUESSEL = 'fsacc.farbschema'

export function gespeichertesFarbschema(): Farbschema {
  try {
    const wert = window.localStorage.getItem(FARBSCHEMA_SCHLUESSEL)
    return wert === 'hell' || wert === 'dunkel' ? wert : 'system'
  } catch {
    return 'system'
  }
}

function merken(wahl: Farbschema): void {
  try {
    if (wahl === 'system') window.localStorage.removeItem(FARBSCHEMA_SCHLUESSEL)
    else window.localStorage.setItem(FARBSCHEMA_SCHLUESSEL, wahl)
  } catch {
    // Privates Fenster: die Wahl gilt fuer diese Sitzung. Kein Grund, irgendetwas abzubrechen.
  }
}

/** Schreibt die Wahl an das <html>-Element. 'system' entfernt das Attribut. */
export function anwenden(wahl: Farbschema, wurzel: HTMLElement): void {
  if (wahl === 'system') wurzel.removeAttribute('data-theme')
  else wurzel.setAttribute('data-theme', wahl === 'dunkel' ? 'dark' : 'light')
}

interface FarbschemaWert {
  wahl: Farbschema
  setzen: (wahl: Farbschema) => void
}

const FarbschemaKontext = createContext<FarbschemaWert | null>(null)

export function FarbschemaProvider({ children }: { children: ReactNode }): ReactElement {
  const [wahl, setWahl] = useState<Farbschema>(() => gespeichertesFarbschema())

  useEffect(() => {
    anwenden(wahl, document.documentElement)
  }, [wahl])

  const setzen = useCallback((neu: Farbschema) => {
    merken(neu)
    setWahl(neu)
  }, [])

  const wert = useMemo<FarbschemaWert>(() => ({ wahl, setzen }), [wahl, setzen])

  return <FarbschemaKontext.Provider value={wert}>{children}</FarbschemaKontext.Provider>
}

export function useFarbschema(): FarbschemaWert {
  const wert = useContext(FarbschemaKontext)
  if (wert === null) throw new Error('useFarbschema außerhalb von FarbschemaProvider verwendet')
  return wert
}
